import { api } from '../config/api';

export interface Perfil {
  id: string;
  nome: string;
  descricao?: string;
  permissoes: Record<string, any>;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
}

export interface PermissaoModulo {
  ver?: boolean;
  criar?: boolean;
  editar?: boolean;
  aprovar?: boolean;
  reprovar?: boolean;
  reagendar?: boolean;
  editar_status?: boolean;
  editar_dados?: boolean;
  registrar_ocorrencias?: boolean;
  alterar_ordem?: boolean;
  gerenciar_perfis?: boolean;
  ver_todos?: boolean;
  ver_propria_filial?: boolean;
  ver_limitado?: boolean;
  ver_proprias?: boolean;
  backup?: boolean;
  logs?: boolean;
  // Permissões específicas do sistema
  aprovar_solicitacao?: boolean;
  reprovar_solicitacao?: boolean;
  solicitar_pedido?: boolean;
  aprovar_solicitacao_agendamento?: boolean;
  reprovar_solicitacao_agendamento?: boolean;
  reagendar_solicitacao?: boolean;
  digitar_pedido?: boolean;
  solicitar_agendamento?: boolean;
  editar_murais?: boolean;
  editar_ordem?: boolean;
  editar_ocorrencia?: boolean;
  editar_prioridade?: boolean;
  // Permissões do módulo de visitas
  gerenciar_agenda?: boolean;
  realizar_checkin?: boolean;
  cancelar?: boolean;
  solicitar?: boolean;
  ver_agenda?: boolean;
  ver_checkin?: boolean;
  ver_solicitacoes?: boolean;
  ver_indicadores?: boolean;
  ver_agendar_visitas?: boolean;
  ver_gestao_visitas?: boolean;
  configurar_horario?: boolean;
  fechar_dia?: boolean;
  finalizar_atendimento?: boolean;
  historico?: boolean;
  gerenciar_popups?: boolean;
}

export interface PerfilPermissoes {
  solicitacao_cadastro?: PermissaoModulo;
  gestao_agendamento?: PermissaoModulo;
  gestao_pedidos?: PermissaoModulo;
  inicio?: PermissaoModulo;
  cargas?: PermissaoModulo;
  usuarios?: PermissaoModulo;
  bi_analitico?: PermissaoModulo;
  area_rca?: PermissaoModulo;
  filiais?: PermissaoModulo;
  configuracoes?: PermissaoModulo;
  logs?: PermissaoModulo;
  visitas?: PermissaoModulo;
  checkbox_d2?: PermissaoModulo;
}

const cachePerfis: Record<string, Perfil | null> = {};

export class PerfisService {
  // Buscar todos os perfis
  static async buscarTodos(): Promise<{ data: Perfil[] | null; error: any }> {
    try {
      const response = await api.get('/perfis');
      
      return { data: response.data || null, error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Erro ao buscar perfis' };
    }
  }

  // Buscar perfil por ID
  static async buscarPorId(id: string): Promise<{ data: Perfil | null; error: any }> {
    if (cachePerfis[id]) return { data: cachePerfis[id], error: null };
    try {
      const response = await api.get(`/perfis/${id}`);
      cachePerfis[id] = response.data || null;
      return { data: cachePerfis[id], error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Erro ao buscar perfil' };
    }
  }

  // Buscar perfil por nome (via API intermediária)
  static async buscarPorNome(nome: string): Promise<{ data: Perfil | null; error: any }> {
    const chave = `nome:${nome}`;
    if (cachePerfis[chave]) return { data: cachePerfis[chave], error: null };
    try {
      const response = await api.get(`/perfis/nome/${encodeURIComponent(nome)}`);
      cachePerfis[chave] = response.data || null;
      return { data: cachePerfis[chave], error: null };
    } catch (error: any) {
      console.error('Erro ao buscar perfil por nome via API:', error);
      return { data: null, error: error.message || 'Erro ao buscar perfil' };
    }
  }

  // Limpar cache de perfis (útil após login/logout)
  static limparCache() {
    Object.keys(cachePerfis).forEach(key => delete cachePerfis[key]);
  }

  // Buscar perfil do usuário logado
  static async buscarMeuPerfil(): Promise<{ data: any; error: any }> {
    try {
      const response = await api.get('/perfis/meu-perfil');
      
      return { data: response.data, error: null };
    } catch (error: any) {
      console.error('Erro ao buscar meu perfil:', error);
      return { data: null, error: error.message || 'Erro ao buscar perfil' };
    }
  }

  // Criar perfil
  static async criar(perfil: Omit<Perfil, 'id' | 'criado_em' | 'atualizado_em'>): Promise<{ data: Perfil | null; error: any }> {
    try {
      const response = await api.post('/perfis', perfil);
      
      return { data: response.data || null, error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Erro ao criar perfil' };
    }
  }

  // Atualizar perfil
  static async atualizar(id: string, perfil: Partial<Perfil>): Promise<{ data: Perfil | null; error: any }> {
    try {
      const response = await api.put(`/perfis/${id}`, {
        ...perfil,
        atualizado_em: new Date().toISOString()
      });
      
      return { data: response.data || null, error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Erro ao atualizar perfil' };
    }
  }

  // Excluir perfil
  static async excluir(id: string): Promise<{ error: any }> {
    try {
      await api.delete(`/perfis/${id}`);
      
      return { error: null };
    } catch (error: any) {
      return { error: error.message || 'Erro ao excluir perfil' };
    }
  }

  // Verificar permissão específica
  static async verificarPermissao(
    perfilId: string, 
    modulo: keyof PerfilPermissoes, 
    permissao: keyof PermissaoModulo
  ): Promise<{ temPermissao: boolean; error: any }> {
    try {
      // Verificar se é UUID ou nome de perfil
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(perfilId);
      
      const { data: perfil, error } = isUUID 
        ? await this.buscarPorId(perfilId)
        : await this.buscarPorNome(perfilId);
      
      if (error || !perfil) {
        console.error('Perfil não encontrado:', perfilId, error);
        return { temPermissao: false, error };
      }

      const permissoes = perfil.permissoes as PerfilPermissoes;
      const moduloPermissoes = permissoes[modulo];
      
      if (!moduloPermissoes) {
        // Módulo simplesmente não se aplica a este perfil (ex.: "logs" para perfil fornecedor).
        // Não é um erro, apenas ausência de permissão para essa área do sistema.
        return { temPermissao: false, error: null };
      }

      const temPermissao = moduloPermissoes[permissao] || false;
      
      // Fallback: se a permissão não existir, tentar variantes comuns
      if (!temPermissao) {
        const fallbacks: Record<string, string> = {
          'aprovar_solicitacao': 'aprovar_solicitacao_agendamento',
          'aprovar_solicitacao_agendamento': 'aprovar_solicitacao',
          'reprovar_solicitacao': 'reprovar_solicitacao_agendamento',
          'reprovar_solicitacao_agendamento': 'reprovar_solicitacao'
        };
        const fallbackKey = fallbacks[permissao as string];
        if (fallbackKey && (moduloPermissoes as any)[fallbackKey]) {
          return { temPermissao: true, error: null };
        }
      }
      
      return { temPermissao, error: null };
    } catch (error) {
      console.error('Erro ao verificar permissão:', error);
      return { temPermissao: false, error };
    }
  }

  // Obter permissões padrão para novos perfis
  static getPermissoesPadrao(): PerfilPermissoes {
    return {
      solicitacao_cadastro: {
        aprovar_solicitacao: false,
        reprovar_solicitacao: false
      },
      gestao_agendamento: {
        ver: false,
        solicitar_pedido: false,
        aprovar_solicitacao: false,
        aprovar_solicitacao_agendamento: false,
        reprovar_solicitacao: false,
        reprovar_solicitacao_agendamento: false,
        reagendar_solicitacao: false
      },
      gestao_pedidos: {
        ver: false,
        digitar_pedido: false
      },
      inicio: {
        ver: false,
        solicitar_agendamento: false,
        editar_murais: false
      },
      cargas: {
        ver: false,
        editar_ordem: false,
        editar_ocorrencia: false,
        editar_prioridade: false,
        editar_status: false,
        configurar_horario: false,
        fechar_dia: false
      },
      usuarios: {
        ver: false,
        criar: false,
        editar: false,
        aprovar: false,
        gerenciar_perfis: false
      },
      bi_analitico: {
        ver: false
      },
      area_rca: {
        ver: false
      },
      filiais: {
        ver: false
      },
      configuracoes: {
        ver: false,
        gerenciar_popups: false
      },
      logs: {
        ver: false
      },
      visitas: {
        ver: false,
        editar: false,
        criar: false,
        aprovar: false,
        reprovar: false,
        reagendar: false,
        cancelar: false,
        solicitar: false,
        ver_agenda: false,
        ver_checkin: false,
        ver_solicitacoes: false,
        ver_indicadores: false,
        ver_agendar_visitas: false,
        ver_gestao_visitas: false,
        gerenciar_agenda: false,
        realizar_checkin: false,
        finalizar_atendimento: false,
        historico: false
      },
      checkbox_d2: {
        ver: false,
        editar: false
      }
    };
  }
}
